import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, money } from "@/lib/api";
import { ProgressPills } from "@/components/ProgressPills";
import { FollowUpStatus } from "@/components/FollowUpStatus";
import { ConsultantDossier } from "@/components/ConsultantDossier";
import { ArrowLeft, Download, Loader2, Mail, Phone, FileText } from "lucide-react";

const TYPE_LABEL = { first_home_buyer: "First Home Buyer", home_buyer: "Home Buyer", investor: "Investor" };
const LOCATION_LABELS = {
  growth: "Growth corridor", schools: "Good schools", childcare: "Childcare",
  transport: "Transport links", work: "Close to work", shops: "Shops & dining",
  parks: "Parks & nature", medical: "Medical & hospitals",
};

function progressFor(session) {
  const d = session?.data || {};
  if (session?.client_type === "investor") {
    return [
      { label: "Discovery", done: !!d.investor_discovery },
      { label: "Locations", done: !!d.investor_location },
      { label: "Floor plan", done: !!d.investor_floorplan },
      { label: "Funding", done: !!d.investor_funding },
      { label: "Reveal", done: (session?.current_stage || 0) >= 6 },
    ];
  }
  return [
    { label: "Discovery", done: !!(d.life_profile || d.discovery_answers) },
    { label: "Location", done: !!d.location },
    { label: "Finance", done: !!d.finance },
    { label: "Budget", done: !!d.budget },
    { label: "Homes", done: !!(d.matches || d.selected_home) },
    { label: "Wealth", done: !!d.wealth },
    { label: "Captured", done: !!(d.profile || d.journey_sent) },
  ];
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [status, setStatusState] = useState("new");
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const dossierRef = useRef(null);

  useEffect(() => {
    api.crmClient(id).then((s) => { setSession(s); setStatusState(s.follow_up_status || "new"); })
      .catch(() => toast.error("Could not load this client."))
      .finally(() => setLoading(false));
  }, [id]);

  const changeStatus = (st) => {
    setStatusState(st);
    api.crmSetStatus(id, st).catch(() => toast.error("Could not update status."));
  };

  const download = async () => {
    setDownloading(true);
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      const name = (session?.client_name || "client").replace(/[^a-z0-9]+/gi, "-");
      await html2pdf().set({
        margin: 0,
        filename: `PropIntellix-Dossier-${name}.pdf`,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
        jsPDF: { unit: "pt", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"] },
      }).from(dossierRef.current).save();
      toast.success("Dossier PDF downloading.");
    } catch (e) {
      console.error("dossier pdf failed", e?.message || e);
      toast.error("Could not generate the dossier.");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-surface text-mutedText">Loading client…</div>;
  if (!session) return <div className="min-h-screen flex items-center justify-center bg-surface text-mutedText">Client not found.</div>;

  const d = session.data || {};
  const buyers = (d.profile?.buyers || []).filter((b) => b.name || b.email || b.mobile);
  const budget = d.budget || {};
  const selected = d.selected_home || {};
  const chosen = (d.matches || {})[selected.kind] || {};
  const ranked = (d.location?.needs || []).map((k, i) => ({ rank: i + 1, label: LOCATION_LABELS[k] || k }));
  const iLoc = d.investor_location || {};
  const isInvestor = session.client_type === "investor";

  return (
    <div className="min-h-screen bg-surface relative">
      <div className="ambient-glow" />
      <div className="relative max-w-4xl mx-auto px-6 py-8">
        <button data-testid="detail-back" onClick={() => navigate("/crm")} className="inline-flex items-center gap-2 text-sm text-mutedText hover:text-forest transition">
          <ArrowLeft size={16} /> Back to clients
        </button>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-brand">{TYPE_LABEL[session.client_type]}</div>
              <h1 className="font-serif text-3xl md:text-4xl text-forest mt-1" data-testid="detail-name">{session.client_name}</h1>
              <p className="text-mutedText text-sm mt-1">Consultant: {session.consultant_name} · Created {session.created_at ? new Date(session.created_at).toLocaleDateString("en-AU") : "—"}</p>
              <div className="mt-3"><FollowUpStatus value={status} onChange={changeStatus} /></div>
            </div>
            <button data-testid="detail-download-dossier" onClick={download} disabled={downloading}
              className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-white text-sm font-medium shadow-xl shadow-brand/25 hover:bg-brand-dark transition disabled:opacity-50">
              {downloading ? <><Loader2 size={16} className="animate-spin" /> Preparing…</> : <><Download size={16} /> Download detail PDF</>}
            </button>
          </div>

          <div className="mt-5 rounded-2xl border border-line bg-white/[0.03] p-5">
            <span className="label-eyebrow text-mutedText text-xs uppercase tracking-widest">Workflow progress</span>
            <div className="mt-3"><ProgressPills progress={progressFor(session)} /></div>
          </div>

          {/* Contacts */}
          <div className="mt-4 rounded-2xl border border-line bg-white/[0.03] p-5" data-testid="detail-contacts">
            <span className="text-xs uppercase tracking-widest text-brand font-semibold">Contacts</span>
            <div className="mt-3 space-y-2">
              {buyers.length ? buyers.map((b, i) => (
                <div key={i} className="flex flex-wrap items-center gap-3 text-sm text-forest">
                  <span className="font-medium">{b.name || `Buyer ${i + 1}`}</span>
                  {b.email && <span className="inline-flex items-center gap-1 text-mutedText"><Mail size={13} /> {b.email}</span>}
                  {b.mobile && <span className="inline-flex items-center gap-1 text-mutedText"><Phone size={13} /> {b.mobile}</span>}
                  {b.contact && <span className="text-xs text-mutedText">prefers {b.contact}</span>}
                </div>
              )) : <p className="text-mutedText text-sm">No contact captured yet.</p>}
            </div>
          </div>

          {/* Snapshot */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {!isInvestor && (chosen.name || selected.name) && (
              <div className="rounded-2xl border border-line bg-white/[0.03] p-5">
                <span className="text-xs uppercase tracking-widest text-brand font-semibold">Chosen home</span>
                <div className="text-forest font-medium mt-2">{selected.name || chosen.name}</div>
                <div className="text-mutedText text-sm mt-1">Home budget {money(budget.home_budget)} · Loan {money(budget.loan_needed)} · LVR {budget.lvr != null ? `${budget.lvr}%` : "—"}</div>
              </div>
            )}
            {isInvestor && iLoc.suburb && (
              <div className="rounded-2xl border border-line bg-white/[0.03] p-5">
                <span className="text-xs uppercase tracking-widest text-brand font-semibold">Target location</span>
                <div className="text-forest font-medium mt-2">{iLoc.suburb} · {iLoc.corridor}</div>
                <div className="text-mutedText text-sm mt-1">Median {money(iLoc.median)} · Yield {iLoc.gross_yield != null ? `${iLoc.gross_yield}%` : "—"} · 12m {iLoc.price_growth_12m != null ? `${iLoc.price_growth_12m}%` : "—"}</div>
              </div>
            )}
            {!isInvestor && ranked.length > 0 && (
              <div className="rounded-2xl border border-line bg-white/[0.03] p-5">
                <span className="text-xs uppercase tracking-widest text-brand font-semibold">Top locations</span>
                <div className="text-mutedText text-sm mt-2">{ranked.slice(0, 4).map((r) => `${r.rank}. ${r.label}`).join(" · ")}</div>
              </div>
            )}
          </div>

          <p className="text-mutedText/70 text-xs mt-6 inline-flex items-center gap-2"><FileText size={13} /> The full dossier (every discovery answer, finance, budget & math) is in the downloadable PDF.</p>
        </motion.div>
      </div>
      <ConsultantDossier ref={dossierRef} session={session} />
    </div>
  );
}
